<div class="ticket-counter-container">
  <!-- Page Header -->
  <div class="page-header">
    <h1 class="page-title">Ticket Counter</h1>
    <p class="page-subtitle">Manage and print ticket batches</p>
  </div>

  <!-- No Batches Message -->
  <div *ngIf="batches.length === 0" class="no-data-container">
    <i class="pi pi-info-circle"></i>
    <p>No ticket batches have been assigned to you yet.</p>
    <small>Please contact your administrator.</small>
  </div>

  <!-- Summary and Batches -->
  <div *ngIf="batches.length > 0" class="batches-table-container">
    <!-- Summary Info (Moved to Top) -->
    <div class="summary-info">
      <div class="info-card">
        <i class="pi pi-list"></i>
        <div class="info-content">
          <span class="info-label">Total Batches</span>
          <span class="info-value">{{ batches.length }}</span>
        </div>
      </div>
      <div class="info-card">
        <i class="pi pi-ticket"></i>
        <div class="info-content">
          <span class="info-label">Total Assigned</span>
          <span class="info-value">{{ getTotalAssignedTickets() }}</span>
        </div>
      </div>
      <div class="info-card">
        <i class="pi pi-check-circle"></i>
        <div class="info-content">
          <span class="info-label">Total Printed</span>
          <span class="info-value">{{ getTotalPrintedTickets() }}</span>
        </div>
      </div>
      <div class="info-card">
        <i class="pi pi-clock"></i>
        <div class="info-content">
          <span class="info-label">Total Remaining</span>
          <span class="info-value">{{ getTotalRemainingTickets() }}</span>
        </div>
      </div>
    </div>
    <!-- Batch Details -->
    <div class="batch-details-section">
      <h2 class="section-title">Batch Details</h2>

      <!-- Mobile Cards View -->
      <div class="batch-cards mobile-only">
        <div *ngFor="let batch of batches" class="batch-card" [class.printing]="isPrinting(batch.batchId)">
          <div class="batch-card-header">
            <h3 class="batch-number">{{ batch.batchNumber }}</h3>
            <span class="status-badge" [ngClass]="{
              'status-assigned': batch.status === 'Assigned',
              'status-in-progress': batch.status === 'InProgress'
            }">
              {{ batch.status }}
            </span>
          </div>

          <div class="batch-card-body">
            <div class="info-row">
              <span class="label">Assigned User:</span>
              <span class="value">{{ batch.assignedUserName }}</span>
            </div>
            <div class="info-row">
              <span class="label">Email:</span>
              <span class="value">{{ batch.assignedUserEmail }}</span>
            </div>
            <div class="info-row">
              <span class="label">Category</span>
              <span class="value">{{ batch?.ticketCategorySummary?.categoryName }}</span> 
            </div>
            <div class="info-row">
              <span class="label">Price</span>
              <span class="value">{{ batch?.ticketCategorySummary?.categoryPrice }}</span>
            </div>

            <div class="counts-row">
              <div class="count-item">
                <span class="count-label">Total</span>
                <span class="count-badge total">{{ batch.assignedTicketCount }}</span>
              </div>
              <div class="count-item">
                <span class="count-label">Printed</span>
                <span class="count-badge printed">{{ batch.printedTicketCount }}</span>
              </div>
              <div class="count-item">
                <span class="count-label">Remaining</span>
                <span class="count-badge remaining">{{ getRemainingTickets(batch) }}</span>
              </div>
            </div>

            <div class="print-controls">
              <div class="quantity-control">
                <button class="qty-btn decrement" (click)="decrementPrintCount(batch.batchId)"
                  [disabled]="isPrinting(batch.batchId) || getPrintCount(batch.batchId) <= 0">
                  <i class="pi pi-minus"></i>
                </button>
                <input type="number" class="print-input" [value]="getPrintCount(batch.batchId)"
                  (input)="updatePrintCount(batch.batchId, +$any($event.target).value)" min="0"
                  [max]="getRemainingTickets(batch)"
                  [disabled]="isPrinting(batch.batchId) || getRemainingTickets(batch) === 0" />
                <button class="qty-btn increment" (click)="incrementPrintCount(batch.batchId)"
                  [disabled]="isPrinting(batch.batchId) || getPrintCount(batch.batchId) >= getRemainingTickets(batch)">
                  <i class="pi pi-plus"></i>
                </button>
              </div>
              <div class="button-group">
                <button pButton type="button" icon="pi pi-print" class="print-button"
                  [disabled]="isPrintDisabled(batch) || getRemainingTickets(batch) === 0"
                  [loading]="isPrinting(batch.batchId)" (click)="printBatchTickets(batch)"
                  style="display: flex; align-items: center; justify-content: center; gap: 0.5rem;">
                  <span class="button-label" style="display: flex; align-items: center; justify-content: center;">Print Tickets</span>
                </button>
                <!-- <button pButton type="button" icon="pi pi-file" class="manual-print-button"
                  severity="secondary"
                  [disabled]="isPrinting(batch.batchId) || getPrintCount(batch.batchId) <= 0 || getPrintCount(batch.batchId) > getRemainingTickets(batch)"
                  (click)="manualPrintBatch(batch)">
                  <span class="button-label">Manual Print</span>
                </button> -->
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Desktop Table View -->
      <div class="table-wrapper desktop-only">
        <table class="batches-table">
          <thead>
            <tr>
              <th>Batch Number</th>
              <th>Assigned User</th>
              <th>Category</th>
              <th>Price</th>
              <th>Total</th>
              <th>Printed</th>
              <th>Remaining</th>
              <th>Status</th>
              <th>Print Quantity</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let batch of batches" [class.printing]="isPrinting(batch.batchId)">
              <td class="batch-number">{{ batch.batchNumber }}</td>
              <td class="user-info">
                <div class="user-name">{{ batch.assignedUserName }}</div>
                <div class="user-email">{{ batch.assignedUserEmail }}</div>
              </td>
              <td class="batch-number text-center">{{ batch?.ticketCategorySummary?.categoryName||'-'}}</td>
              <td class="batch-number text-center">{{ batch?.ticketCategorySummary?.categoryPrice ||'-'}}</td>
              <td class="text-center">
                <span class="count-badge total">{{ batch.assignedTicketCount }}</span>
              </td>
              <td class="text-center">
                <span class="count-badge printed">{{ batch.printedTicketCount }}</span>
              </td>
              <td class="text-center">
                <span class="count-badge remaining">{{ getRemainingTickets(batch) }}</span>
              </td>
              <td>
                <span class="status-badge" [ngClass]="{
                  'status-assigned': batch.status === 'Assigned',
                  'status-in-progress': batch.status === 'InProgress'
                }">
                  {{ batch.status }}
                </span>
              </td>
              <td class="print-input-cell">
                <div class="quantity-control">
                  <button class="qty-btn decrement" (click)="decrementPrintCount(batch.batchId)"
                    [disabled]="isPrinting(batch.batchId) || getPrintCount(batch.batchId) <= 0">
                    <i class="pi pi-minus"></i>
                  </button>
                  <input type="number" class="print-input" [value]="getPrintCount(batch.batchId)"
                    (input)="updatePrintCount(batch.batchId, +$any($event.target).value)" min="0"
                    [max]="getRemainingTickets(batch)"
                    [disabled]="isPrinting(batch.batchId) || getRemainingTickets(batch) === 0" />
                  <button class="qty-btn increment" (click)="incrementPrintCount(batch.batchId)"
                    [disabled]="isPrinting(batch.batchId) || getPrintCount(batch.batchId) >= getRemainingTickets(batch)">
                    <i class="pi pi-plus"></i>
                  </button>
                </div>
              </td>
              <td class="action-cell">
                <div class="button-group">
                  <button pButton type="button" icon="pi pi-print" class="print-button"
                    [disabled]="isPrintDisabled(batch) || getRemainingTickets(batch) === 0"
                    [loading]="isPrinting(batch.batchId)" (click)="printBatchTickets(batch)">
                    <span class="button-label">Print</span>
                  </button>
                  <!-- <button pButton type="button" icon="pi pi-file" class="manual-print-button"
                    severity="secondary"
                    [disabled]="isPrinting(batch.batchId) || getPrintCount(batch.batchId) <= 0 || getPrintCount(batch.batchId) > getRemainingTickets(batch)"
                    (click)="manualPrintBatch(batch)">
                    <span class="button-label">Manual</span>
                  </button> -->
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</div>

<p-toast></p-toast>